import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react'
import { flushSync } from 'react-dom'
import { saveBlingLogisticaItem, setBlingStatusOverride, getLogisticaOrdem, saveLogisticaOrdem, getParadas, saveParada, deleteParada } from '../utils/storage'
import { criarParada, TIPOS_PARADA, labelTipoParada, estiloTipoParada } from '../data/paradas'
import { useOrders } from '../hooks/useOrders'
import { KANBAN_COLUNAS, CIDADES_ABAS, detectarCidadeAba } from '../data/kanban'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Filter, Trash2, Eye, EyeOff, AlertTriangle, Wrench, Split, MapPin, Plus, X, Check, Search } from 'lucide-react'
import { criarPedidoAjuste, labelMotivo, hojeYmd } from '../data/ajustes'
import { AbrirAjusteModal } from '../components/AjustePanel'
import { TerceirizadosPopover, ComprasTerceirizadasButton } from '../components/Terceirizados'
import { LinhaLogisticaMobile } from '../components/LinhaLogisticaMobile'
import { toggleTerceirizado } from '../data/terceirizados'
// (ADICIONAIS_BOOLEAN/QUANTIDADE removidos — a descrição comercial vem
// de descricaoMovel() que já cobre puxador/base/adicionais/observações.)
import { usePedidosFiltros } from '../hooks/usePedidosFiltros'
import { FiltroDrawer } from '../components/FiltroDrawer'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formatCurrency, formatTelefone, expandirCoresParaEntrega, descricaoMovel } from '../utils/fmt'

// ── Constantes ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  CADASTRADO: { bg: '#94A3B8', text: '#fff' },
  FABRICANDO: { bg: '#FBBF24', text: '#111' },
  EXPEDICAO:  { bg: '#4ADE80', text: '#111' },
  ROTA:       { bg: '#FB923C', text: '#fff' },
  ENTREGUE:   { bg: '#F9A8D4', text: '#9D174D' },
  AJUSTE:     { bg: '#93C5FD', text: '#1E40AF' },
  A_FABRICAR: { bg: '#94A3B8', text: '#fff' },
  FABRICADO:  { bg: '#93C5FD', text: '#1E40AF' },
  POS_VENDA:  { bg: '#F9A8D4', text: '#9D174D' },
}

const FORMAS_LABEL = { pix: 'Pix', dinheiro: 'Dinheiro', credito: 'Crédito', debito: 'Débito' }

const HORARIOS = [
  '8h - 10h','9h - 11h','10h - 12h','11h - 13h',
  '13h - 15h','14h - 16h','15h - 17h','16h - 18h',
]
const HORARIO_ORDEM = Object.fromEntries(HORARIOS.map((h,i) => [h, i+1]))

const CONFIRMACOES = [
  { value: '',           label: '—',           bg: '#F1F5F9', border: '#CBD5E1', text: '#94A3B8' },
  { value: 'Marcado',    label: 'Marcado',      bg: '#E5E7EB', border: '#9CA3AF', text: '#374151' },
  { value: 'Confirmado', label: '✓ Confirmado', bg: '#DCFCE7', border: '#86EFAC', text: '#166534' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateDisplay(d) {
  if (!d) return '—'
  try { return format(new Date(d + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR }) } catch { return d }
}

// Gera o resumo do pedido pra exibir na Logística e no PDF do dia.
// Usa descricaoMovel() do fmt.js — que consulta o mapa DESCRICAO_COMERCIAL
// pra usar nome + sufixo comercial (ex: "Roupeiro Branco (200x60x210) com
// duas portas, maleiro interno, cabideiro e 2 espaços inferiores").
// Modelos sem entrada no mapa caem no nome canônico com adicionais em prosa.
// Pedido do gerente de logística: nomenclatura detalhada e consistente com
// o que a Daíze definiu no PDF/WhatsApp de orçamento.
function gerarResumo(moveis = []) {
  return moveis.map(m => descricaoMovel(m)).join(' · ')
}

// Endereço enxuto pra Google Maps: rua + número + bairro (sem cidade nem
// complemento). Usa campos separados quando disponíveis (pedido local);
// senão tenta parsear a string completa do Bling até o bairro.
function enderecoParaMaps(cliente) {
  if (!cliente) return ''
  if (cliente.rua && cliente.numero) {
    return [cliente.rua, cliente.numero, cliente.bairro].filter(Boolean).join(', ')
  }
  const endereco = cliente.endereco || ''
  if (!endereco) return ''
  const partes = endereco.split(',').map(s => s.trim()).filter(Boolean)
  if (cliente.bairro) {
    const idx = partes.findIndex(p => p.toLowerCase() === cliente.bairro.toLowerCase())
    if (idx > 0) return partes.slice(0, idx + 1).join(', ')
  }
  // Fallback Bling sem bairro mapeado: rua, número (2 primeiras partes)
  return partes.slice(0, 2).join(', ')
}

function extrairBairroCidade(cliente) {
  if (cliente?.bairro && cliente?.cidade) return `${cliente.bairro} - ${cliente.cidade}`
  if (cliente?.bairro) return cliente.bairro
  const endereco = cliente?.endereco || ''
  if (!endereco) return ''
  const partes = endereco.split(',').map(p => p.trim()).filter(Boolean)
  if (partes.length >= 2) {
    const cidade = partes[partes.length - 1].replace(/\s*-\s*[A-Z]{2}$/, '').trim()
    const bairro = partes[partes.length - 2]
    if (cidade && bairro) return `${bairro} - ${cidade}`
  }
  return partes[partes.length - 1] || endereco
}

// ── Célula de texto inline editável ──────────────────────────────────────────
function InlineText({ value, onSave, placeholder, className = '' }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(value || '')
  const ref                   = useRef(null)
  useEffect(() => { if (editing) { setDraft(value || ''); ref.current?.focus() } }, [editing])
  function commit() { setEditing(false); if (draft !== value) onSave(draft) }
  if (editing) {
    return (
      <input
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }}
        className={`w-full text-xs border border-[#F5C200] rounded px-1 py-0.5 outline-none bg-white ${className}`}
      />
    )
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className={`block cursor-text text-xs leading-snug ${value ? '' : 'text-slate-300 italic'} ${className}`}
    >
      {value || placeholder}
    </span>
  )
}

// ── Célula multi-linha editável (resumo, endereço longo) ────────────────────
// Diferenças do InlineText: usa <textarea>, Enter quebra linha (não comita),
// expande conforme o conteúdo, blur ou Esc encerram a edição.
function InlineTextarea({ value, onSave, placeholder, className = '', rows = 2 }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(value || '')
  const ref                   = useRef(null)
  useEffect(() => { if (editing) { setDraft(value || ''); ref.current?.focus() } }, [editing])
  function commit() { setEditing(false); if (draft !== value) onSave(draft) }
  if (editing) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') { setDraft(value || ''); setEditing(false) } }}
        className={`w-full text-xs border border-[#F5C200] rounded px-1 py-1 outline-none bg-white resize-y ${className}`}
      />
    )
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className={`block cursor-text text-xs leading-relaxed ${value ? '' : 'text-slate-300 italic'} ${className}`}
    >
      {value || placeholder}
    </span>
  )
}

// ── Célula de data editável (corrigido: onBlur não fecha ao abrir calendário) ─
function InlineDate({ value, onSave }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(value || '')
  const ref = useRef(null)
  useEffect(() => { setDraft(value || '') }, [value])

  if (editing) {
    return (
      <input ref={ref} type="date" value={draft}
        autoFocus
        onChange={e => {
          setDraft(e.target.value)
          if (e.target.value) { onSave(e.target.value); setEditing(false) }
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={e => { if (e.key === 'Escape') setEditing(false) }}
        className="text-xs border border-[#F5C200] rounded px-1 py-0.5 outline-none w-28"
      />
    )
  }
  return (
    <span onClick={() => setEditing(true)}
      className={`block cursor-pointer text-xs ${value ? 'text-slate-700' : 'text-slate-300 italic'}`}>
      {formatDateDisplay(value) || 'dd/mm/aaaa'}
    </span>
  )
}

// ── Cabeçalho da tabela ───────────────────────────────────────────────────────
function TabelaCabecalho() {
  return (
    <div className="flex items-stretch bg-[#111111] rounded-t-xl text-[#F5C200] text-[10px] font-bold uppercase tracking-wide">
      <div className="w-[110px] shrink-0 px-3 py-2.5">Status</div>
      <div className="w-[140px] shrink-0 px-3 py-2.5 border-l border-white/10">Bairro</div>
      <div className="w-[100px] shrink-0 px-3 py-2.5 border-l border-white/10">Data Entrega</div>
      <div className="w-[110px] shrink-0 px-3 py-2.5 border-l border-white/10">Horário</div>
      <div className="w-[120px] shrink-0 px-3 py-2.5 border-l border-white/10">Confirmação</div>
      <div className="w-[170px] shrink-0 px-3 py-2.5 border-l border-white/10">Nome do Cliente</div>
      <div className="flex-1 px-3 py-2.5 border-l border-white/10">Resumo do Pedido</div>
      <div className="w-[220px] shrink-0 px-3 py-2.5 border-l border-white/10">Endereço de Entrega</div>
      <div className="w-[130px] shrink-0 px-3 py-2.5 border-l border-white/10">Contato</div>
      <div className="w-[116px] shrink-0 border-l border-white/10" />
    </div>
  )
}

// ── Linha de pedido ───────────────────────────────────────────────────────────
function linhaIgual(prev, next) {
  const p = prev.pedido, n = next.pedido
  return (
    p.id                 === n.id                 &&
    p.status             === n.status             &&
    p.prazo              === n.prazo              &&
    p.valorTotal         === n.valorTotal         &&
    p.horarioEntrega     === n.horarioEntrega     &&
    p.confirmacaoEntrega === n.confirmacaoEntrega &&
    p.bairro             === n.bairro             &&
    p.resumo             === n.resumo             &&
    p.atencao            === n.atencao            &&
    p.prazoOriginal      === n.prazoOriginal      &&
    p.tipoAjuste         === n.tipoAjuste         &&
    p.tipoFilho          === n.tipoFilho          &&
    (p.terceirizadosEncomendados || []).join(',') === (n.terceirizadosEncomendados || []).join(',') &&
    p.cliente?.nome      === n.cliente?.nome      &&
    p.cliente?.contato   === n.cliente?.contato   &&
    p.cliente?.endereco  === n.cliente?.endereco  &&
    prev.isLast          === next.isLast          &&
    prev.onChange        === next.onChange        &&
    prev.onAbrirAjuste   === next.onAbrirAjuste   &&
    prev.onDelete        === next.onDelete
  )
}

const LinhaLogistica = memo(function LinhaLogistica({ pedido, onChange, onAbrirAjuste, onDelete, isLast, dragHandleProps }) {
  const s         = STATUS_STYLE[pedido.status] || STATUS_STYLE.CADASTRADO
  // Expande siglas de cor pra vista da entrega (BRC → Branco). Pedidos
  // Bling vêm com o resumo abreviado; entregador demorava fazendo a
  // associação. Só afeta a tela da Logística e o PDF do dia — o dado
  // salvo continua o original.
  const resumo    = useMemo(() => expandirCoresParaEntrega(pedido.resumo || gerarResumo(pedido.moveis)), [pedido.resumo, pedido.moveis])
  const bairroVal = useMemo(() => pedido.bairro || extrairBairroCidade(pedido.cliente), [pedido.bairro, pedido.cliente])
  const conf      = useMemo(() => CONFIRMACOES.find(c => c.value === (pedido.confirmacaoEntrega || '')) || CONFIRMACOES[0], [pedido.confirmacaoEntrega])
  const ehAjuste  = !!pedido.tipoAjuste
  const ehFilho   = !!pedido.tipoFilho

  function update(campo, valor) {
    const atualizado = { ...pedido, [campo]: valor }
    // Atraso automático: compara contra prazoCadastro (imutável, gravado
    // no NewOrder). Fallback pro prazo atual quando o pedido é antigo e
    // ainda não tem prazoCadastro.
    //
    // Regra:
    //   novo > prazoCadastro → atrasado (grava prazoOriginal se não tem)
    //   novo ≤ prazoCadastro → NÃO é atraso, LIMPA prazoOriginal
    //     (gerente adiantou e desistiu — volta ao original não é atraso)
    if (campo === 'prazo' && valor && pedido.prazo) {
      const prazoBase = pedido.prazoCadastro || pedido.prazoOriginal || pedido.prazo
      if (!pedido.prazoCadastro) atualizado.prazoCadastro = prazoBase
      if (valor > prazoBase) {
        if (!pedido.prazoOriginal) atualizado.prazoOriginal = prazoBase
      } else {
        // Voltou pro prazo original ou adiantou — limpa flag de atraso
        atualizado.prazoOriginal = ''
      }
    }
    onChange(atualizado)
  }

  // Atualiza um campo dentro de pedido.cliente (contato, endereco, ...).
  // Telefone passa pela máscara antes de salvar.
  function updateCliente(campo, valor) {
    const v = campo === 'contato' ? formatTelefone(valor) : valor
    onChange({ ...pedido, cliente: { ...(pedido.cliente || {}), [campo]: v } })
  }

  // Dois estados visuais distintos:
  //   - Atraso (vermelho): prazo foi adiado em relação ao prazoOriginal salvo.
  //   - Atenção manual (laranja): gerente clicou no botão do alerta.
  //   Vermelho prevalece quando ambos estão ativos.
  const atrasada       = pedido.prazoOriginal && pedido.prazoOriginal !== pedido.prazo
  const atencaoManual  = !!pedido.atencao && !atrasada

  return (
    <div
      {...dragHandleProps}
      className={`flex items-stretch border-x transition-colors cursor-grab active:cursor-grabbing
        ${ehAjuste
          ? `${LINHA_AJUSTE} hover:brightness-95 border-slate-200`
          : atrasada
            ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500 border-slate-200'
            : atencaoManual
              ? 'bg-orange-50 hover:bg-orange-100 border-l-4 border-l-orange-500 border-slate-200'
              : 'bg-white hover:bg-[#FFFDF5] border-slate-200'}
        ${isLast ? 'border-b rounded-b-xl' : 'border-b border-slate-100'}`}
    >

      {/* Status — dropdown padrão pra todos (AJ também segue Kanban) */}
      <div className="w-[110px] shrink-0 flex items-center px-2 py-2">
        <select
          value={pedido.status}
          onChange={e => update('status', e.target.value)}
          style={{ backgroundColor: s.bg, color: s.text }}
          className="w-full px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide cursor-pointer border-0 outline-none appearance-none"
        >
          {KANBAN_COLUNAS.map(col => (
            <option key={col.id} value={col.id}>{col.label}</option>
          ))}
        </select>
      </div>

      {/* Bairro */}
      <div className="w-[140px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-center">
        <InlineText
          value={bairroVal}
          placeholder="—"
          onSave={v => update('bairro', v)}
          className={bairroVal ? 'text-slate-700 font-medium' : ''}
        />
      </div>

      {/* Data — mostra "antes: dd/MM" embaixo se houve adiamento */}
      <div className="w-[100px] shrink-0 px-3 py-2 border-l border-slate-100 flex flex-col items-start justify-center">
        <InlineDate value={pedido.prazo} onSave={v => update('prazo', v)} />
        {pedido.prazoOriginal && pedido.prazoOriginal !== pedido.prazo && (
          <span
            className="text-[10px] text-red-600 italic mt-0.5"
            title={`Prazo original antes do atraso: ${formatDateDisplay(pedido.prazoOriginal)}`}
          >
            antes: {formatDateDisplay(pedido.prazoOriginal)}
          </span>
        )}
      </div>

      {/* Horário */}
      <div className="w-[110px] shrink-0 px-2 py-2 border-l border-slate-100 flex items-center">
        <select
          value={pedido.horarioEntrega || ''}
          onChange={e => update('horarioEntrega', e.target.value)}
          className="w-full text-xs text-slate-600 border-0 outline-none bg-transparent cursor-pointer appearance-none"
        >
          <option value="">—</option>
          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>

      {/* Confirmação */}
      <div className="w-[120px] shrink-0 px-2 py-2 border-l border-slate-100 flex items-center">
        <select
          value={pedido.confirmacaoEntrega || ''}
          onChange={e => update('confirmacaoEntrega', e.target.value)}
          style={{ backgroundColor: conf.bg, color: conf.text, borderColor: conf.border }}
          className="w-full px-2 py-0.5 rounded text-[11px] font-semibold border cursor-pointer outline-none appearance-none"
        >
          {CONFIRMACOES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {/* Cliente */}
      <div className="w-[170px] shrink-0 px-3 py-2 border-l border-slate-100 flex flex-col justify-center">
        <p className="text-xs font-semibold text-slate-800 leading-snug flex items-center gap-1">
          {ehAjuste && <Wrench size={10} className="text-amber-600 shrink-0" />}
          {ehFilho  && <Split  size={10} className="text-indigo-600 shrink-0" />}
          {pedido.cliente?.nome || '—'}
        </p>
        <p className="text-[10px] font-mono text-slate-400 mt-0.5"
          title={ehAjuste ? labelMotivo(pedido.motivoAjuste) + (pedido.observacaoAjuste ? ' — ' + pedido.observacaoAjuste : '')
            : ehFilho   ? `Parte ${pedido.letraParte} de ${pedido.totalPartes} do pedido #${pedido.pedidoMaeNumero}`
            : undefined}
        >
          {pedido.numero}
        </p>
      </div>

      {/* Resumo (editável — textarea expansível) */}
      <div className="flex-1 px-3 py-2 border-l border-slate-100 flex items-start">
        <InlineTextarea
          value={resumo}
          placeholder="—"
          onSave={v => update('resumo', v)}
          className="text-slate-600"
          rows={2}
        />
      </div>

      {/* Endereço (editável) */}
      <div className="w-[220px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-start">
        <InlineTextarea
          value={pedido.cliente?.endereco || ''}
          placeholder="—"
          onSave={v => updateCliente('endereco', v)}
          className="text-slate-600"
          rows={2}
        />
      </div>

      {/* Contato (editável com máscara) */}
      <div className="w-[130px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-center">
        <InlineText
          value={formatTelefone(pedido.cliente?.contato) || ''}
          placeholder="—"
          onSave={v => updateCliente('contato', v)}
          className="text-slate-700"
        />
      </div>

      {/* Atenção + Terceirizados + Abrir ajuste + Excluir */}
      <div className="w-[116px] shrink-0 flex items-center justify-center gap-0.5 border-l border-slate-100">
        <button
          type="button"
          onClick={() => update('atencao', !pedido.atencao)}
          title={pedido.atencao ? 'Remover marcação' : 'Marcar atenção (cliente com problema, ajuste, etc.)'}
          className={`p-1.5 rounded-lg transition-colors
            ${pedido.atencao
              ? 'text-orange-600 bg-orange-100 hover:bg-orange-200'
              : 'text-slate-200 hover:text-orange-500 hover:bg-orange-50'}`}
        >
          <AlertTriangle size={13} fill={pedido.atencao ? 'currentColor' : 'none'} />
        </button>
        {/* Terceirizados — só pra pedidos que têm pelo menos um (pé/base/metalon) */}
        <TerceirizadosPopover pedido={pedido} onChange={onChange} />
        {/* Abrir ajuste — só pra pedidos entregues/pós-venda que NÃO são eles mesmos um AJ */}
        {(pedido.status === 'ENTREGUE' || pedido.status === 'POS_VENDA') && !ehAjuste && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onAbrirAjuste?.(pedido) }}
            title="Abrir ajuste (pedido com problema, retornar pra Mini, consertar e re-entregar)"
            className="p-1.5 rounded-lg text-slate-200 hover:text-amber-600 hover:bg-amber-50 transition-colors"
          >
            <Wrench size={13} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(pedido.id)}
          title="Excluir pedido"
          className="p-1.5 rounded-lg text-slate-200 hover:text-red-400 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  )
}, linhaIgual)

// ── Página principal ──────────────────────────────────────────────────────────
// Estilo visual da linha do pedido fantasma de ajuste — diferencia das
// entregas normais sem mudar o fluxo de status (que é o mesmo).
const LINHA_AJUSTE = 'border-l-4 border-l-amber-400 bg-amber-50/30'

// ── Linha de Parada (tarefa avulsa do dia) ──────────────────────────────────
function LinhaParada({ parada, onChange, onDelete, isLast }) {
  const estilo = estiloTipoParada(parada.tipo)

  function updateCampo(campo, valor) {
    onChange({ ...parada, [campo]: valor })
  }

  const bgBase = parada.concluida
    ? 'bg-emerald-50/40 hover:bg-emerald-100/40'
    : 'bg-violet-50/40 hover:bg-violet-100/40'

  return (
    <div className={`flex items-stretch border-x border-l-4 border-l-violet-400 border-slate-200
        ${bgBase}
        ${isLast ? 'border-b rounded-b-xl' : 'border-b border-slate-100'}`}>

      {/* Status — badge violeta "PARADA · TIPO" + checkbox concluída */}
      <div className="w-[110px] shrink-0 flex items-center gap-1 px-2 py-2">
        <button
          type="button"
          onClick={() => updateCampo('concluida', !parada.concluida)}
          title={parada.concluida ? 'Desmarcar' : 'Marcar concluída'}
          className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors
            ${parada.concluida
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'bg-white border-slate-300 hover:border-emerald-400'}`}
        >
          {parada.concluida && <Check size={10} strokeWidth={3} />}
        </button>
        <span
          style={{ backgroundColor: estilo.cor, color: estilo.textCor }}
          className="flex-1 text-center px-1 py-1 rounded text-[9px] font-bold uppercase tracking-wide"
          title="Movimento avulso da rotina"
        >
          🔁 {labelTipoParada(parada.tipo)}
        </span>
      </div>

      {/* Bairro — vazio */}
      <div className="w-[140px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-center text-xs text-slate-300">
        —
      </div>

      {/* Data — editável (input date) */}
      <div className="w-[100px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-center">
        <InlineDate value={parada.data} onSave={v => updateCampo('data', v)} />
      </div>

      {/* Horário — select igual ao pedido */}
      <div className="w-[110px] shrink-0 px-2 py-2 border-l border-slate-100 flex items-center">
        <select
          value={parada.horarioEntrega || ''}
          onChange={e => updateCampo('horarioEntrega', e.target.value)}
          className="w-full text-xs text-slate-600 border-0 outline-none bg-transparent cursor-pointer appearance-none"
        >
          <option value="">—</option>
          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>

      {/* Confirmação — vazio */}
      <div className="w-[120px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-center text-xs text-slate-300">
        —
      </div>

      {/* Cliente — "PARADA" rótulo discreto */}
      <div className="w-[170px] shrink-0 px-3 py-2 border-l border-slate-100 flex flex-col justify-center">
        <p className="text-xs font-bold text-violet-700 leading-snug">PARADA AVULSA</p>
        <p className="text-[10px] text-slate-400 mt-0.5 italic">rotina logística</p>
      </div>

      {/* Resumo — descrição editável */}
      <div className="flex-1 px-3 py-2 border-l border-slate-100 flex items-start">
        <InlineTextarea
          value={parada.descricao}
          placeholder="Ex: Pegar metalon na Lemadeiras"
          onSave={v => updateCampo('descricao', v)}
          className={parada.concluida ? 'text-slate-500 line-through' : 'text-slate-700 font-medium'}
          rows={2}
        />
      </div>

      {/* Endereço — editável */}
      <div className="w-[220px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-start">
        <InlineTextarea
          value={parada.endereco || ''}
          placeholder="Endereço (opcional — vira link Maps no PDF)"
          onSave={v => updateCampo('endereco', v)}
          className="text-slate-600"
          rows={2}
        />
      </div>

      {/* Contato — vazio */}
      <div className="w-[130px] shrink-0 px-3 py-2 border-l border-slate-100 flex items-center text-xs text-slate-300">
        —
      </div>

      {/* Ações — só excluir */}
      <div className="w-[116px] shrink-0 flex items-center justify-center gap-0.5 border-l border-slate-100">
        <button
          type="button"
          onClick={() => onDelete(parada.id)}
          title="Remover parada"
          className="p-1.5 rounded-lg text-slate-200 hover:text-red-400 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  )
}

// ── Form inline pra adicionar Parada num dia ────────────────────────────────
function AddParadaInline({ data, onCancel, onConfirm }) {
  const [descricao, setDescricao] = useState('')
  const [endereco,  setEndereco]  = useState('')
  const [tipo,      setTipo]      = useState('passar')
  const [horario,   setHorario]   = useState('')

  function confirmar() {
    if (!descricao.trim()) return
    onConfirm(descricao, tipo, endereco, horario)
    setDescricao('')
    setEndereco('')
    setHorario('')
  }

  return (
    <div className="px-3 py-2.5 bg-violet-100/50 border-x border-violet-200 space-y-2">
      <div className="flex items-center gap-2">
        <select
          value={tipo}
          onChange={e => setTipo(e.target.value)}
          className="text-[10px] font-bold uppercase rounded border border-violet-300 px-2 py-1 bg-white text-violet-800 outline-none cursor-pointer"
        >
          {TIPOS_PARADA.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <select
          value={horario}
          onChange={e => setHorario(e.target.value)}
          className="text-[10px] font-bold rounded border border-violet-300 px-2 py-1 bg-white text-violet-800 outline-none cursor-pointer"
          title="Horário"
        >
          <option value="">— horário —</option>
          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <input
          autoFocus
          type="text"
          value={descricao}
          onChange={e => setDescricao(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') confirmar()
            if (e.key === 'Escape') onCancel()
          }}
          placeholder="Ex: Pegar metalon na Lemadeiras"
          className="flex-1 text-sm text-slate-700 bg-white border border-violet-300 rounded px-3 py-1.5 outline-none focus:border-violet-500"
        />
        <button
          onClick={confirmar}
          disabled={!descricao.trim()}
          className="text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check size={12} className="inline mr-1" />
          Adicionar
        </button>
        <button
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600 p-1.5"
        >
          <X size={14} />
        </button>
      </div>
      <div className="flex items-center gap-2 ml-[58px]">
        <MapPin size={11} className="text-violet-400 shrink-0" />
        <input
          type="text"
          value={endereco}
          onChange={e => setEndereco(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') confirmar()
            if (e.key === 'Escape') onCancel()
          }}
          placeholder="Endereço (opcional — vira link do Maps no PDF)"
          className="flex-1 text-xs text-slate-600 bg-white/70 border border-violet-200 rounded px-2.5 py-1 outline-none focus:border-violet-400"
        />
      </div>
    </div>
  )
}

export function Logistica() {
  const { pedidos, salvar, salvarSilencioso, remover, refresh } = useOrders()
  const [showFiltro, setShowFiltro]           = useState(false)
  const [confirmDelete, setConfirmDelete]     = useState(null)
  const [cidadeAba, setCidadeAba]             = useState('TODAS')
  const [mostrarEntregue, setMostrarEntregue] = useState(false)
  const [ordemManual, setOrdemManual]         = useState(() => getLogisticaOrdem())
  const [paradas, setParadas]                 = useState(() => getParadas())
  // Inline add-form aberto pra uma data específica (null = nenhuma)
  const [addParadaData, setAddParadaData]     = useState(null)

  useEffect(() => { refresh() }, [refresh])

  // Posiciona a tela direto na data de hoje quando o gerente liga "Entregues"
  // (entregas passadas viram visíveis, a lista cresce; sem isso ele cai no topo
  // lá em janeiro). Scroll INSTANTÂNEO — a página já aparece na posição certa,
  // o gerente rola livre pra cima (passado) ou pra baixo (futuro) a partir daí.
  // Pequeno delay deixa o DOM re-renderizar antes de buscar o elemento.
  useEffect(() => {
    if (!mostrarEntregue) return
    const hoje = hojeYmd()
    const t = setTimeout(() => {
      const el = document.getElementById(`grupo-${hoje}`)
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' })
      } else {
        // Sem grupo pra hoje exatamente: tenta o mais próximo no futuro.
        // (caso comum no fim de semana sem entregas marcadas)
        const todos = [...document.querySelectorAll('[id^="grupo-2"]')]
        const proximo = todos.find(n => {
          const d = n.id.replace('grupo-', '')
          return d >= hoje
        })
        if (proximo) proximo.scrollIntoView({ behavior: 'auto', block: 'start' })
      }
    }, 80)
    return () => clearTimeout(t)
  }, [mostrarEntregue])

  // Helpers de Paradas — persiste + atualiza state local
  const adicionarParada = useCallback((data, descricao, tipo, endereco, horarioEntrega) => {
    if (!descricao?.trim()) return
    const p = criarParada({
      data,
      descricao:      descricao.trim(),
      tipo,
      endereco:       (endereco || '').trim(),
      horarioEntrega: horarioEntrega || '',
    })
    saveParada(p)
    setParadas(prev => [...prev, p])
    setAddParadaData(null)
  }, [])
  const atualizarParada = useCallback((p) => {
    saveParada(p)
    setParadas(prev => prev.map(x => x.id === p.id ? p : x))
  }, [])
  const removerParada = useCallback((id) => {
    deleteParada(id)
    setParadas(prev => prev.filter(x => x.id !== id))
  }, [])

  const { filtros, totalAtivos, limpar, toggle, set, filtrar } = usePedidosFiltros()

  const handleChange = useCallback((atualizado) => {
    const pedidoAnterior = pedidos.find(p => p.id === atualizado.id)
    if (atualizado.status === 'AJUSTE' && pedidoAnterior?.status !== 'AJUSTE') {
      atualizado.historico = [...(atualizado.historico || []),
        { tipo: 'ajuste', data: new Date().toISOString().split('T')[0] }]
    } else if (pedidoAnterior?.status === 'AJUSTE' && atualizado.status === 'ENTREGUE') {
      atualizado.historico = [...(atualizado.historico || []),
        { tipo: 'reentregue', data: new Date().toISOString().split('T')[0] }]
    }

    if (String(atualizado.id).startsWith('bling-')) {
      setBlingStatusOverride(atualizado.id, atualizado.status)
      saveBlingLogisticaItem(atualizado.id, {
        horarioEntrega:       atualizado.horarioEntrega,
        confirmacaoEntrega:   atualizado.confirmacaoEntrega,
        enderecoEntrega:      atualizado.enderecoEntrega,
        observacoesLogistica: atualizado.observacoesLogistica,
        prazoOverride:        atualizado.prazo,
        prazoOriginal:        atualizado.prazoOriginal || '',
        atencao:              !!atualizado.atencao,
        // Edição livre da Logística (sobrescreve o que vem do Bling)
        resumoOverride:       atualizado.resumo            || '',
        contatoOverride:      atualizado.cliente?.contato  || '',
        enderecoOverride:     atualizado.cliente?.endereco || '',
        // Tracking de compras terceirizadas
        terceirizadosEncomendados: atualizado.terceirizadosEncomendados || [],
      })
      refresh()
    } else {
      salvar(atualizado)
    }
  }, [salvar, refresh, pedidos])

  const handleDelete = useCallback((id) => { remover(id); setConfirmDelete(null) }, [remover])

  // Marca/desmarca um terceirizado como encomendado (toggle).
  // Reaproveita handleChange pra persistir (local ou Bling override).
  const marcarTerceirizado = useCallback((p, idTerc) => {
    const novo = toggleTerceirizado(p, idTerc)
    handleChange({ ...p, terceirizadosEncomendados: novo })
  }, [handleChange])

  // Estado do modal "Abrir ajuste" (id do pedido alvo, null quando fechado)
  const [pedidoAbrindoAjuste, setPedidoAbrindoAjuste] = useState(null)

  // Cria o pedido FANTASMA de ajuste a partir do modal — novo pedido com
  // numero AJ-..., tipoAjuste=true, link pro original. Original fica intacto.
  // AJ segue o fluxo padrão do Kanban (CADASTRADO → FABRICANDO → etc.).
  const confirmarAberturaAjuste = useCallback(({ motivo, observacao, prazo, moveisSelecionados }) => {
    if (!pedidoAbrindoAjuste) return
    const orig = pedidos.find(x => x.id === pedidoAbrindoAjuste)
    if (!orig) { setPedidoAbrindoAjuste(null); return }
    const fantasma = criarPedidoAjuste(orig, { motivo, observacao, prazo, moveisSelecionados })
    salvar(fantasma)
    setPedidoAbrindoAjuste(null)
  }, [pedidoAbrindoAjuste, pedidos, salvar])

  const dataEfetiva = useCallback((p) => p.prazo || '__sem_data__', [])

  const pedidos2026 = useMemo(() => pedidos.filter(p =>
    // Pedido mãe dividido sai da Logística — quem assume são os filhos
    !p.dividido &&
    p.prazo?.startsWith('2026')
  ), [pedidos])

  const pedidosFiltradosCidade = useMemo(() => {
    let lista = pedidos2026
    if (!mostrarEntregue) lista = lista.filter(p => p.status !== 'ENTREGUE' && p.status !== 'POS_VENDA')
    if (cidadeAba !== 'TODAS') lista = lista.filter(p => detectarCidadeAba(p) === cidadeAba)
    return filtrar(lista)
  }, [pedidos2026, mostrarEntregue, cidadeAba, filtrar])

  const listagem = useMemo(() => {
    const posMap = new Map(ordemManual.map((id, i) => [id, i]))
    return [...pedidosFiltradosCidade].sort((a, b) => {
      const da = dataEfetiva(a), db = dataEfetiva(b)
      if (da !== db) {
        // __em_conserto__ e __sem_data__ vão pro fim
        const aFim = da.startsWith('__'), bFim = db.startsWith('__')
        if (aFim && !bFim) return 1
        if (!aFim && bFim) return -1
        if (aFim && bFim) return da < db ? -1 : 1
        return da < db ? -1 : 1
      }
      const ha = HORARIO_ORDEM[a.horarioEntrega] || 999
      const hb = HORARIO_ORDEM[b.horarioEntrega] || 999
      if (ha !== hb) return ha - hb
      const pa = posMap.has(a.id) ? posMap.get(a.id) : 999999
      const pb = posMap.has(b.id) ? posMap.get(b.id) : 999999
      return pa - pb
    })
  }, [pedidosFiltradosCidade, ordemManual, dataEfetiva])

  // Mapa: data → lista de paradas, pra mesclar com pedidos no agrupamento
  const paradasPorData = useMemo(() => {
    const map = new Map()
    paradas.forEach(p => {
      if (!map.has(p.data)) map.set(p.data, [])
      map.get(p.data).push(p)
    })
    // Ordena por ordem dentro de cada dia
    map.forEach(arr => arr.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)))
    return map
  }, [paradas])

  const grupos = useMemo(() => {
    // Cada grupo agora tem uma única lista `itens` com pedidos E paradas
    // misturados, ordenados pelo horário. Item: { tipo: 'pedido'|'parada', dado }
    const map = new Map()
    listagem.forEach(p => {
      const d = dataEfetiva(p)
      if (!map.has(d)) map.set(d, { itens: [], qtdPedidos: 0, qtdParadas: 0 })
      map.get(d).itens.push({ tipo: 'pedido', dado: p })
      map.get(d).qtdPedidos++
    })
    paradasPorData.forEach((lista, data) => {
      if (!map.has(data)) map.set(data, { itens: [], qtdPedidos: 0, qtdParadas: 0 })
      lista.forEach(par => {
        map.get(data).itens.push({ tipo: 'parada', dado: par })
        map.get(data).qtdParadas++
      })
    })
    // Sort dos itens dentro de cada dia: por horário (pedidos e paradas juntos).
    // Sem horário cai pro fim do dia. Paradas concluídas vão pro fim entre as
    // sem horário também.
    map.forEach(grupo => {
      grupo.itens.sort((a, b) => {
        const ha = HORARIO_ORDEM[a.dado.horarioEntrega] || 999
        const hb = HORARIO_ORDEM[b.dado.horarioEntrega] || 999
        if (ha !== hb) return ha - hb
        return 0
      })
    })
    // Ordena os GRUPOS por data
    return [...map.entries()].sort(([a], [b]) => {
      const aFim = a.startsWith('__'), bFim = b.startsWith('__')
      if (aFim && !bFim) return 1
      if (!aFim && bFim) return -1
      return a < b ? -1 : 1
    })
  }, [listagem, dataEfetiva, paradasPorData])

  function onDragEnd(result) {
    const { draggableId, destination, source } = result
    if (!destination) return
    if (destination.droppableId === source.droppableId && destination.index === source.index) return

    const srcDate  = source.droppableId
    const destDate = destination.droppableId
    const crossDate = srcDate !== destDate && destDate !== '__sem_data__'

    // Se mudou de data: persiste o novo prazo no localStorage ANTES de qualquer render.
    if (crossDate) {
      const pedido = pedidos.find(p => p.id === draggableId)
      if (pedido) {
        // Mesma lógica de atraso do editor inline: compara contra
        // prazoCadastro (imutável). Se voltou pro original, limpa
        // prazoOriginal (não é atraso — gerente adiantou e desistiu).
        const prazoBase = pedido.prazoCadastro || pedido.prazoOriginal || pedido.prazo
        const virouAtraso = pedido.prazo && destDate > prazoBase
        const extrasAtraso = pedido.tipoAjuste
          ? {} // AJ nunca registra atraso — data de reentrega é livre
          : virouAtraso
            ? { prazoOriginal: pedido.prazoOriginal || prazoBase, prazoCadastro: prazoBase }
            : { prazoOriginal: '', prazoCadastro: prazoBase }
        if (String(pedido.id).startsWith('bling-')) {
          saveBlingLogisticaItem(pedido.id, { prazoOverride: destDate, ...extrasAtraso })
        } else {
          salvarSilencioso({ ...pedido, prazo: destDate, ...extrasAtraso })
        }
      }
    }

    // Reconstrói a ordem manual considerando todos os grupos (só pedidos
    // entram na ordem; paradas têm ordem separada por data)
    const novaOrdem = grupos.reduce((acc, [d, grupo]) => {
      const ids = grupo.itens.filter(i => i.tipo === 'pedido').map(i => i.dado.id)
      if (d === srcDate && d === destDate) {
        const g = [...ids]
        g.splice(g.indexOf(draggableId), 1)
        g.splice(destination.index, 0, draggableId)
        return [...acc, ...g]
      } else if (d === srcDate) {
        return [...acc, ...ids.filter(id => id !== draggableId)]
      } else if (d === destDate) {
        const g = [...ids.filter(id => id !== draggableId)]
        g.splice(destination.index, 0, draggableId)
        return [...acc, ...g]
      }
      return [...acc, ...ids]
    }, [])

    saveLogisticaOrdem(novaOrdem)

    // flushSync garante que o React re-renderiza ANTES do @hello-pangea/dnd
    // fazer o cleanup do DOM — sem isso o item "snapeia" de volta à posição original
    flushSync(() => {
      setOrdemManual(novaOrdem)
      if (crossDate) refresh()   // lê o localStorage já atualizado com o novo prazo
    })
  }

  function gerarPdfDia(data) {
    const pedidosDoDia = listagem.filter(p => p.prazo === data)
    const paradasDoDia = (paradasPorData.get(data) || [])
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pageW = doc.internal.pageSize.getWidth()

    doc.setFillColor(17, 17, 17)
    doc.rect(0, 0, pageW, 18, 'F')
    doc.setFillColor(245, 194, 0)
    doc.rect(0, 16, pageW, 2, 'F')
    doc.setTextColor(245, 194, 0)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.text('MINI Marcenaria — Entregas do Dia', 12, 11)
    doc.setTextColor(200, 200, 200)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text(formatDateDisplay(data), pageW - 12, 11, { align: 'right' })

    // Mescla pedidos + paradas, sortados por horário (ambos têm horarioEntrega).
    // Sem horário cai pro fim.
    const itensDoDia = [
      ...pedidosDoDia.map(p   => ({ tipo: 'pedido', dado: p })),
      ...paradasDoDia.map(par => ({ tipo: 'parada', dado: par })),
    ].sort((a, b) => {
      const ha = HORARIO_ORDEM[a.dado.horarioEntrega] || 999
      const hb = HORARIO_ORDEM[b.dado.horarioEntrega] || 999
      return ha - hb
    })

    // Endereços do Maps na mesma ordem dos itens
    const enderecosMaps = itensDoDia.map(it =>
      it.tipo === 'parada' ? (it.dado.endereco || null) : enderecoParaMaps(it.dado.cliente)
    )

    // Quais linhas são paradas (pra estilo no didParseCell)
    const linhasParada = new Set(itensDoDia.map((it, i) => it.tipo === 'parada' ? i : -1).filter(i => i >= 0))

    const rows = itensDoDia.map((it, i) => {
      if (it.tipo === 'parada') {
        const par = it.dado
        return [
          i + 1,
          par.horarioEntrega || '—',
          `🔁 PARADA · ${labelTipoParada(par.tipo).toUpperCase()}`,
          par.endereco || '—',
          par.endereco ? 'Abrir' : '—',
          '—',
          par.descricao || '—',
          '—', '—',
        ]
      }
      const p = it.dado
      return [
        i + 1,
        p.horarioEntrega || '—',
        p.cliente?.nome || '—',
        p.cliente?.endereco || '—',
        'Abrir',
        formatTelefone(p.cliente?.contato) || '—',
        expandirCoresParaEntrega(p.resumo || gerarResumo(p.moveis)) || '—',
        p.valorPendenteEntrega > 0 ? formatCurrency(p.valorPendenteEntrega) : '—',
        FORMAS_LABEL[p.formaPagamento] || p.formaPagamento || '—',
      ]
    })

    autoTable(doc, {
      startY: 22,
      head: [['#', 'Horário', 'Cliente', 'Endereço', 'Mapa', 'Contato', 'Itens', 'Pendente', 'Pagamento']],
      body: rows,
      styles: { fontSize: 7, cellPadding: 2, textColor: [17,17,17] },
      headStyles: { fillColor: [17,17,17], textColor: [245,194,0], fontSize: 7, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 8,  halign: 'center' },
        1: { cellWidth: 22 },
        2: { cellWidth: 40 },
        3: { cellWidth: 60 },
        4: { cellWidth: 14, halign: 'center' },    // Mapa
        5: { cellWidth: 28 },
        6: { cellWidth: 'auto' },
        7: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
        8: { cellWidth: 25 },
      },
      didParseCell: (cell) => {
        const ehParada = cell.section === 'body' && linhasParada.has(cell.row.index)
        // Linhas de parada: fundo violeta claro + texto cinza-violeta
        if (ehParada) {
          cell.cell.styles.fillColor = [243, 232, 255]  // violet-100
          cell.cell.styles.textColor = [76, 29, 149]    // violet-900
          if (cell.column.index === 2) cell.cell.styles.fontStyle = 'bold'
        }
        // Coluna "Mapa" em azul (visual de link) — em pedidos e em paradas
        // que tenham endereço preenchido (cell.raw === 'Abrir')
        if (cell.section === 'body' && cell.column.index === 4 && cell.raw === 'Abrir') {
          cell.cell.styles.textColor = [37, 99, 235]
          cell.cell.styles.fontStyle = 'bold'
        }
      },
      didDrawCell: (cell) => {
        // Hyperlink Maps em qualquer linha que tenha endereço no array
        if (cell.section === 'body' && cell.column.index === 4) {
          const enderecoMaps = enderecosMaps[cell.row.index]
          if (enderecoMaps) {
            const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(enderecoMaps)}`
            doc.link(cell.cell.x, cell.cell.y, cell.cell.width, cell.cell.height, { url })
          }
        }
      },
      alternateRowStyles: { fillColor: [248,250,252] },
      margin: { left: 10, right: 10 },
    })

    doc.save(`Entregas-${data}.pdf`)
  }

  return (
    <div className="px-3 md:px-4 pb-6 transition-all" style={showFiltro && window.innerWidth >= 768 ? { paddingRight: 312 } : {}}>

      {/* Cabeçalho + Abas — sticky logo abaixo do Navbar fixo (h-14 = 56px).
          Fundo branco sólido, sem translucência, pra não deixar a tabela
          aparecer por baixo. */}
      <div className="sticky top-14 z-20 bg-white -mx-4 px-4 pt-4 pb-3 mb-4 border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Logística de Produção</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {listagem.length} pedido{listagem.length !== 1 ? 's' : ''}
              {totalAtivos > 0 && ` · ${totalAtivos} filtro${totalAtivos > 1 ? 's' : ''} ativo${totalAtivos > 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setMostrarEntregue(v => !v)}
              className={`btn-secondary text-sm ${mostrarEntregue ? 'ring-2 ring-pink-300' : ''}`}
            >
              {mostrarEntregue ? <Eye size={14} /> : <EyeOff size={14} />}
              Entregues
            </button>
            {/* Compras terceirizadas — só pedidos da região JP */}
            <ComprasTerceirizadasButton
              pedidos={pedidos2026.filter(p => detectarCidadeAba(p) === 'JP')}
              onMarcar={(p, idTerc) => marcarTerceirizado(p, idTerc)}
            />
            {/* PDF: dropdown de datas */}
            <select
              className="btn-secondary text-sm cursor-pointer"
              value=""
              onChange={e => { if (e.target.value) gerarPdfDia(e.target.value) }}
            >
              <option value="">📄 PDF do dia…</option>
              {[...new Set(listagem.map(p => p.prazo).filter(Boolean))].sort().map(d => (
                <option key={d} value={d}>{formatDateDisplay(d)}</option>
              ))}
            </select>
            <button
              onClick={() => setShowFiltro(o => !o)}
              className={`btn-secondary text-sm ${showFiltro ? 'ring-2 ring-[#F5C200]' : ''}`}
            >
              <Filter size={14} />
              Filtrar
              {totalAtivos > 0 && (
                <span className="ml-1 bg-[#F5C200] text-[#111111] text-[10px] font-bold px-1.5 py-0.5 rounded-full">{totalAtivos}</span>
              )}
            </button>
          </div>
        </div>

        {/* Buscador — filtra por nome do cliente, número, bairro, endereço, telefone, etc.
            Liga direto em filtros.busca (já existe no usePedidosFiltros: sem acento,
            multi-palavra AND, telefone sem máscara). */}
        <div className="relative mt-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={filtros.busca}
            onChange={e => set('busca', e.target.value)}
            placeholder="Buscar cliente, número, bairro, telefone…"
            className="w-full md:max-w-md pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white placeholder:text-slate-400 focus:border-[#F5C200] focus:ring-2 focus:ring-[#F5C200]/20 transition-colors"
          />
          {filtros.busca && (
            <button
              type="button"
              onClick={() => set('busca', '')}
              title="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Abas de cidade — dentro do mesmo sticky */}
        <div className="flex gap-1 mt-3 bg-slate-100 rounded-xl p-1 w-fit">
          {CIDADES_ABAS.map(aba => (
            <button
              key={aba.id}
              onClick={() => setCidadeAba(aba.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                ${cidadeAba === aba.id ? 'bg-white text-[#111111] shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {aba.label}
            </button>
          ))}
        </div>
      </div>

      <FiltroDrawer
        open={showFiltro}
        onClose={() => setShowFiltro(false)}
        filtros={filtros}
        toggle={toggle}
        set={set}
        limpar={limpar}
        totalAtivos={totalAtivos}
        pedidos={pedidos2026}
        sections={{ confirmacao: true, horario: true }}
      />

      {/* Conteúdo */}
      {listagem.length === 0 ? (
        <div className="text-center py-20 text-slate-400 border border-slate-200 rounded-xl">
          <p className="text-sm">Nenhum pedido encontrado</p>
          {totalAtivos > 0 && <button onClick={limpar} className="mt-3 text-xs text-[#CC8800] hover:underline">Limpar filtros</button>}
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Desktop: tabela horizontal (md+) | Mobile: cards verticais (<md) */}
          <div className="hidden md:block overflow-x-auto rounded-xl shadow-sm">
            <div className="min-w-[1200px]">
              {grupos.map(([data, { itens, qtdPedidos, qtdParadas }]) => {
                // Map de índice DENTRO da lista pra cada pedido (pra Draggable index).
                // Paradas não são draggable, então não consomem índice.
                const idxPedido = new Map()
                let counter = 0
                itens.forEach(it => {
                  if (it.tipo === 'pedido') { idxPedido.set(it.dado.id, counter); counter++ }
                })

                return (
                <div key={data} id={`grupo-${data}`} className="scroll-mt-60">
                  {/* Separador de data + botão de Parada */}
                  <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border border-slate-200 mt-2 first:mt-0 rounded-t-xl">
                    <span className="text-xs font-bold text-slate-600">
                      {data === '__sem_data__' ? 'Sem data' : formatDateDisplay(data)}
                    </span>
                    <span className="text-xs text-slate-400">
                      {qtdPedidos} pedido{qtdPedidos !== 1 ? 's' : ''}
                      {qtdParadas > 0 && <> · {qtdParadas} parada{qtdParadas !== 1 ? 's' : ''}</>}
                    </span>
                    {data !== '__sem_data__' && data !== '__em_conserto__' && (
                      <button
                        onClick={() => setAddParadaData(addParadaData === data ? null : data)}
                        className="ml-auto text-xs font-semibold bg-violet-100 hover:bg-violet-200 text-violet-700 px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors"
                        title="Adicionar parada/movimento avulso nesse dia"
                      >
                        <Plus size={11} />
                        Parada
                      </button>
                    )}
                  </div>

                  {/* Form inline pra adicionar parada nesse dia */}
                  {addParadaData === data && (
                    <AddParadaInline
                      data={data}
                      onCancel={() => setAddParadaData(null)}
                      onConfirm={(desc, tipo, end, hora) => adicionarParada(data, desc, tipo, end, hora)}
                    />
                  )}

                  {(qtdPedidos > 0 || qtdParadas > 0) && <TabelaCabecalho />}
                  <Droppable droppableId={data}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={snapshot.isDraggingOver ? 'bg-yellow-50' : ''}
                      >
                        {itens.map((it, i) => {
                          const isLast = i === itens.length - 1
                          if (it.tipo === 'parada') {
                            return (
                              <LinhaParada
                                key={it.dado.id}
                                parada={it.dado}
                                onChange={atualizarParada}
                                onDelete={removerParada}
                                isLast={isLast}
                              />
                            )
                          }
                          const p = it.dado
                          return (
                            <Draggable key={p.id} draggableId={p.id} index={idxPedido.get(p.id)}>
                              {(drag, snap) => (
                                <div
                                  ref={drag.innerRef}
                                  {...drag.draggableProps}
                                  className={snap.isDragging ? 'opacity-90 shadow-lg' : ''}
                                >
                                  <LinhaLogistica
                                    pedido={p}
                                    onChange={handleChange}
                                    onAbrirAjuste={pedido => setPedidoAbrindoAjuste(pedido.id)}
                                    onDelete={id => setConfirmDelete(id)}
                                    isLast={isLast}
                                    dragHandleProps={drag.dragHandleProps}
                                  />
                                </div>
                              )}
                            </Draggable>
                          )
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              )})}
            </div>
          </div>

          {/* Mobile: cards verticais agrupados por data (sem tabela horizontal,
              sem drag-and-drop — touch é confuso com isso) */}
          <div className="md:hidden space-y-3">
            {grupos.map(([data, { itens, qtdPedidos, qtdParadas }]) => (
              <div key={data} id={`grupo-${data}`} className="scroll-mt-32">
                {/* Header de data */}
                <div className="px-2 py-2 flex items-center gap-2 bg-slate-100 rounded-lg mb-2 sticky top-12 z-10">
                  <span className="text-xs font-bold text-slate-700">
                    {data === '__sem_data__' ? 'Sem data' : formatDateDisplay(data)}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {qtdPedidos} ped{qtdParadas > 0 && ` · ${qtdParadas} parada${qtdParadas !== 1 ? 's' : ''}`}
                  </span>
                  {data !== '__sem_data__' && data !== '__em_conserto__' && (
                    <button
                      onClick={() => setAddParadaData(addParadaData === data ? null : data)}
                      className="ml-auto text-[10px] font-bold bg-violet-100 text-violet-700 px-2 py-1 rounded active:bg-violet-200"
                    >
                      <Plus size={10} className="inline mr-0.5" />
                      Parada
                    </button>
                  )}
                </div>

                {addParadaData === data && (
                  <AddParadaInline
                    data={data}
                    onCancel={() => setAddParadaData(null)}
                    onConfirm={(desc, tipo, end, hora) => adicionarParada(data, desc, tipo, end, hora)}
                  />
                )}

                {itens.map((it) => {
                  if (it.tipo === 'parada') {
                    return (
                      <LinhaParada
                        key={it.dado.id}
                        parada={it.dado}
                        onChange={atualizarParada}
                        onDelete={removerParada}
                        isLast={false}
                      />
                    )
                  }
                  return (
                    <LinhaLogisticaMobile
                      key={it.dado.id}
                      pedido={it.dado}
                      onChange={handleChange}
                      onAbrirAjuste={pedido => setPedidoAbrindoAjuste(pedido.id)}
                      onDelete={id => setConfirmDelete(id)}
                      statusOptions={KANBAN_COLUNAS.map(c => [c.id, c.label])}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Legenda */}
      <div className="mt-4 flex flex-wrap gap-3">
        {KANBAN_COLUNAS.map(col => {
          const s = STATUS_STYLE[col.id] || STATUS_STYLE.CADASTRADO
          return (
            <span key={col.id} className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.bg }} />
              {col.label}
            </span>
          )
        })}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-slate-800 mb-2">Excluir pedido?</h3>
            <p className="text-sm text-slate-500 mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1 justify-center">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} className="btn-danger flex-1 justify-center">Excluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Abrir Ajuste — disparado pelo botão wrench da linha */}
      {pedidoAbrindoAjuste && (
        <AbrirAjusteModal
          pedido={pedidos.find(p => p.id === pedidoAbrindoAjuste)}
          onClose={() => setPedidoAbrindoAjuste(null)}
          onConfirm={confirmarAberturaAjuste}
        />
      )}

    </div>
  )
}
